import { useEffect, useRef, type MutableRefObject } from "react";

const STICKY_BOTTOM_THRESHOLD_PX = 96;

// Юзер у самого низа списка (в пределах sticky-порога)?
function isNearStickyBottom(container: HTMLElement, thresholdPx: number): boolean {
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distance <= thresholdPx;
}

// Событие `load` / `loadeddata` прилетело от <img> / <video>?
function isMediaElement(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    return el?.tagName === "IMG" || el?.tagName === "VIDEO";
}

interface UseStickyScrollAnchorParams {
    messageCount: number;
    /**
     * Id последнего сообщения ленты. Именно он, а не длина, означает «пришло
     * новое»: догрузка истории тоже удлиняет ленту, но добавляет её СВЕРХУ —
     * скроллить вниз в этот момент значило бы вырвать пользователя из места,
     * куда он домотал.
     */
    lastMessageId: string | undefined;
    /** Локальные ошибки (useChatErrorsStore). */
    errorsCount: number;
    /** In-flight джобы (queued/in_progress/recovering). */
    activeCount: number;
    /** Ошибочные джобы (failed/expired). */
    erroredCount: number;
    /** Поднят, пока догруженная порция вставляется и позиция восстанавливается. */
    isPrependingRef: MutableRefObject<boolean>;
}

// Sticky-bottom поведение списка сообщений: пока isStickyBottomRef=true, любой
// новый layout (сообщение, карточка прогресса, догрузка media) переякоривает к
// низу. Сбрасывается, когда юзер сам прокрутил вверх (wheel/touch/keydown), и
// восстанавливается, когда он долистал обратно к низу. Возвращает refs для
// контейнера, нижнего якоря и флага sticky (используется nav-полоской и
// useMessageGroupCollapse).
export function useStickyScrollAnchor({
    messageCount,
    lastMessageId,
    errorsCount,
    activeCount,
    erroredCount,
    isPrependingRef,
}: UseStickyScrollAnchorParams) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);
    const didInitialScrollRef = useRef(false);
    const isStickyBottomRef = useRef(true);

    function scrollToBottom(behavior: ScrollBehavior) {
        endRef.current?.scrollIntoView({ behavior, block: "end" });
    }

    // Первый скролл к низу при mount или когда messages подъехали после
    // initial empty render (reload + refetch). "instant" — smooth ловит
    // layout shift и приземляется не туда.
    useEffect(() => {
        if (messageCount === 0) return;
        if (didInitialScrollRef.current) return;
        scrollToBottom("instant");
        didInitialScrollRef.current = true;
    }, [messageCount]);

    // На новое сообщение: если sticky-bottom — скроллим. Зависимость по id
    // последнего, а не по длине: догрузка истории длину меняет, «последнее
    // сообщение» — нет.
    useEffect(() => {
        if (!didInitialScrollRef.current) return;
        if (!isStickyBottomRef.current) return;
        scrollToBottom("smooth");
    }, [lastMessageId]);

    // На появление нового in-flight job или ошибки принудительно возвращаем
    // sticky-режим и скроллим вниз — карточка прогресса/ошибки должна быть в
    // кадре сразу, даже если новая высота добавилась после того, как
    // smooth-scroll по messageCount уже завершился.
    useEffect(() => {
        if (!didInitialScrollRef.current) return;
        if (errorsCount === 0 && erroredCount === 0 && activeCount === 0) return;
        isStickyBottomRef.current = true;
        scrollToBottom("smooth");
    }, [errorsCount, erroredCount, activeCount]);

    // Layout shift'ы: ResizeObserver на контейнер ловит изменения высоты
    // контента (MessageProgress, догрузка <img>/<video>). Если пользователь не
    // уезжал вверх — переякориваем к низу.
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const reanchorIfSticky = () => {
            // Прирост высоты от догруженной истории — не повод ехать вниз:
            // её вставили СВЕРХУ, позицию восстанавливает useLoadOlderMessages.
            if (isPrependingRef.current) return;
            if (isStickyBottomRef.current) scrollToBottom("instant");
        };

        // Пользователь прокрутил вверх вручную → выходим из sticky-mode; если
        // сам долистал обратно к низу — снова sticky. wheel/touchmove/keydown —
        // явные интенты, в отличие от scroll-event (его генерит и smooth-скролл).
        const syncStickyWithScrollPosition = () => {
            queueMicrotask(() => {
                isStickyBottomRef.current = isNearStickyBottom(container, STICKY_BOTTOM_THRESHOLD_PX);
            });
        };

        // Default-обработка wheel происходит после listener'а. Если дождаться
        // microtask, пользователь всё ещё формально у нижней границы, а
        // догружающееся в этот момент превью возвращает его обратно вниз.
        const handleWheel = (event: WheelEvent) => {
            if (event.deltaY < 0) {
                isStickyBottomRef.current = false;
                return;
            }
            syncStickyWithScrollPosition();
        };

        const handleKeydown = (event: KeyboardEvent) => {
            if (
                event.key === "ArrowUp" ||
                event.key === "PageUp" ||
                event.key === "Home" ||
                (event.key === " " && event.shiftKey)
            ) {
                isStickyBottomRef.current = false;
                return;
            }
            syncStickyWithScrollPosition();
        };

        const resizeObserver = new ResizeObserver(reanchorIfSticky);
        resizeObserver.observe(container);

        const handleMediaLoad = (event: Event) => {
            if (isMediaElement(event.target)) reanchorIfSticky();
        };

        container.addEventListener("wheel", handleWheel, { passive: true });
        container.addEventListener("touchmove", syncStickyWithScrollPosition, { passive: true });
        container.addEventListener("keydown", handleKeydown);
        // capture: true чтобы поймать `load` от <img> (он не bubbles).
        container.addEventListener("load", handleMediaLoad, { capture: true });
        container.addEventListener("loadeddata", handleMediaLoad, { capture: true });

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("touchmove", syncStickyWithScrollPosition);
            container.removeEventListener("keydown", handleKeydown);
            container.removeEventListener("load", handleMediaLoad, { capture: true });
            container.removeEventListener("loadeddata", handleMediaLoad, { capture: true });
            resizeObserver.disconnect();
        };
    }, [isPrependingRef]);

    return { scrollRef, endRef, isStickyBottomRef };
}
